import { useTranslation } from '@pancakeswap/localization'
import { Box, Breadcrumbs, Button, Card, ErrorState, Flex, Heading, LoadingState, NextLinkFromReactRouter, Text } from '@pancakeswap/uikit'
import { useMemo } from 'react'
import styled from 'styled-components'
import { getBlockExploreLink } from 'utils'
import { DEX_LABEL, useInfoRoute, useTokenDetail } from 'state/infoHub/hooks'
import { PriceChartEntry, TokenData } from 'state/info/types'
import ChartCard from 'views/Info/components/InfoCharts/ChartCard'
import TransactionTable from 'views/Info/components/InfoTables/TransactionsTable'
import Percent from 'views/Info/components/Percent'
import { CurrencyLogo } from 'views/Info/components/CurrencyLogo'
import { DataStateBar } from './DataStateBar'
import { History } from './History'
import { num, shortAddress, usd } from './format'
import { toLegacyTransactions } from './PoolDetailView'
import { pancakePoolsHref } from './pancakeLinks'
import { PoolStatTable } from './tables'

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
const Tile = styled(Box)`
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  min-width: 0;
  overflow-wrap: anywhere;
`

export const TokenDetailView: React.FC = () => {
  const { t } = useTranslation()
  const { dex, version, address, hrefFor } = useInfoRoute()
  const { detail, error, isLoading, retry } = useTokenDetail(dex, version, address)
  const token = detail?.token.data
  const legacyToken = useMemo<TokenData | undefined>(
    () =>
      token
        ? {
            exists: true,
            name: token.name,
            symbol: token.symbol,
            address: token.address,
            decimals: token.decimals,
            volumeUSD: token.volume24hUSD ?? 0,
            volumeUSDChange: token.volume24hChange ?? 0,
            volumeUSDWeek: token.volume7dUSD ?? 0,
            txCount: token.txCount24h,
            liquidityToken: token.liquidityToken,
            liquidityUSD: token.liquidityUSD ?? 0,
            liquidityUSDChange: 0,
            priceUSD: token.priceUSD ?? 0,
            priceUSDChange: token.priceChange24h ?? 0,
            priceUSDChangeWeek: 0,
          }
        : undefined,
    [token],
  )
  const priceSeries = useMemo<PriceChartEntry[] | undefined>(
    () => detail?.prices.data?.map((p) => ({ time: p.time, open: p.priceUSD, close: p.priceUSD, high: p.priceUSD, low: p.priceUSD })),
    [detail],
  )
  const transactions = useMemo(() => (detail?.transactions.data ? toLegacyTransactions(detail.transactions.data) : undefined), [detail])
  if (!dex || !address) return null
  return (
    <Box>
      <Breadcrumbs mb="16px">
        <NextLinkFromReactRouter to="/info">{t('Info')}</NextLinkFromReactRouter>
        {dex === 'pancakeswap' ? (
          // no PancakeSwap tab any more: the way back is the token's CADINUdex page
          <NextLinkFromReactRouter to={`/info/cadinu/tokens/${address}`}>{token ? token.symbol : shortAddress(address)}</NextLinkFromReactRouter>
        ) : (
          <NextLinkFromReactRouter to={hrefFor(dex, version, 'tokens')}>
            {DEX_LABEL[dex]} {t('tokens')}
          </NextLinkFromReactRouter>
        )}
        <Text>{dex === 'pancakeswap' ? t('PancakeSwap pools') : token ? token.symbol : shortAddress(address)}</Text>
      </Breadcrumbs>
      {isLoading && !detail && <LoadingState rows={4} label={t('Loading token')} />}
      {error && !detail && <ErrorState title={t('Could not load token')} description={error.message} onRetry={retry} retryLabel={t('Retry')} />}
      {detail && !token && <ErrorState title={t('Token not found in this DEX/version')} description={detail.token.meta.error} onRetry={retry} retryLabel={t('Retry')} tone="warning" />}
      {token && (
        <>
          <Flex alignItems="center" flexWrap="wrap" style={{ gap: 8 }} mb="4px">
            <CurrencyLogo address={token.address} size="32px" />
            <Heading scale="lg" as="h1">
              {token.symbol}
            </Heading>
            <Text color="textSubtle">{token.name}</Text>
            <Button as="a" href={getBlockExploreLink(token.address, 'token', token.chainId)} target="_blank" rel="noreferrer" scale="sm" variant="text">
              {shortAddress(token.address)} ↗
            </Button>
            <Flex ml="auto" style={{ gap: 8 }}>
              <Button as={NextLinkFromReactRouter} to={`/add/${token.address}`} scale="sm" variant="secondary" data-testid="ih-add">
                {t('Add liquidity')}
              </Button>
              <Button as={NextLinkFromReactRouter} to={`/swap?outputCurrency=${token.address}`} scale="sm" data-testid="ih-trade">
                {t('Trade')}
              </Button>
            </Flex>
          </Flex>
          <Text fontSize="22px" bold mb="12px" color={token.priceUSD === null ? 'failure' : 'text'} title={token.priceSource ?? 'no price path'}>
            {token.priceUSD === null ? '— ' : `$${token.priceUSD < 0.001 ? token.priceUSD.toExponential(4) : num(token.priceUSD)} `}
            <Percent value={token.priceChange24h ?? undefined} fontSize="14px" />
            <Text as="span" fontSize="12px" color="textSubtle" ml="8px">
              {token.priceSource ? `${t('price source')}: ${token.priceSource}` : t('no pool with a priced counter-asset')}
            </Text>
          </Text>
          <DataStateBar meta={detail!.token.meta} onRetry={retry} />
          <Box mt="12px">
            <Grid>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('Liquidity')}
                </Text>
                <Text fontSize="20px" bold color={token.liquidityUSD === null ? 'failure' : 'text'}>
                  {usd(token.liquidityUSD)}
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {num(token.liquidityToken)} {token.symbol} {t('across')} {token.poolCount} {t('pools')}
                </Text>
              </Tile>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('Volume 24H')}
                </Text>
                <Text fontSize="20px" bold color={token.volume24hUSD === null ? 'failure' : 'text'}>
                  {usd(token.volume24hUSD)} <Percent value={token.volume24hChange ?? undefined} fontSize="12px" />
                </Text>
              </Tile>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('Volume 7D')}
                </Text>
                <Text fontSize="20px" bold color={token.volume7dUSD === null ? 'failure' : 'text'}>
                  {usd(token.volume7dUSD)}
                </Text>
              </Tile>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('Swaps 24H')}
                </Text>
                <Text fontSize="20px" bold>
                  {token.txCount24h}
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {t('summed over the token’s pools')}
                </Text>
              </Tile>
            </Grid>
          </Box>
          <Box mt="24px">
            {dex === 'cadinu' ? (
              <>
                <History scope="token" id={token.address} version={version} />
                {legacyToken && priceSeries && priceSeries.length > 0 && (
                  <Box mt="16px">
                    <ChartCard variant="token" priceOnly chartData={[]} tokenData={legacyToken} tokenPriceData={priceSeries} />
                    <Text fontSize="12px" color="textSubtle" mt="4px">
                      {t('price')}: {detail!.prices.meta.coverage ?? detail!.prices.meta.error}
                    </Text>
                  </Box>
                )}
              </>
            ) : (
              <>
                {detail!.chart.data && detail!.chart.data.length > 0 && legacyToken ? (
                  <>
                    <ChartCard variant="token" chartData={detail!.chart.data} tokenData={legacyToken} tokenPriceData={priceSeries} />
                    <Text fontSize="12px" color="textSubtle" mt="4px">
                      {t('History')}: {detail!.chart.meta.coverage} · {t('price')}: {detail!.prices.meta.coverage ?? detail!.prices.meta.error}
                    </Text>
                  </>
                ) : (
                  <Card>
                    <Box p="16px">
                      <Heading scale="md" mb="8px">
                        {t('History')}
                      </Heading>
                      <DataStateBar meta={detail!.chart.meta} compact />
                    </Box>
                  </Card>
                )}

              </>
            )}
          </Box>
          <Flex justifyContent="space-between" alignItems="baseline" flexWrap="wrap" mt="32px" mb="12px" style={{ gap: 8 }}>
            <Heading scale="lg">{dex === 'pancakeswap' ? t('PancakeSwap pools') : t('Pools')}</Heading>
            {/* CADINU and CBON: their PancakeSwap pools (the old PancakeSwap tab) stay one tap away */}
            {dex === 'cadinu' && pancakePoolsHref(token.address) && (
              <NextLinkFromReactRouter to={pancakePoolsHref(token.address) as string} data-testid="ih-pancake-pools">
                {t('%symbol% pools on PancakeSwap', { symbol: token.symbol })} →
              </NextLinkFromReactRouter>
            )}
            {dex === 'pancakeswap' && (
              <NextLinkFromReactRouter to={`/info/cadinu/tokens/${token.address}`} data-testid="ih-cadinu-pools">
                {t('%symbol% pools on CADINUdex', { symbol: token.symbol })} →
              </NextLinkFromReactRouter>
            )}
          </Flex>
          <Card>
            <PoolStatTable pools={detail!.pools.data} meta={detail!.pools.meta} dex={dex} version={version} onRetry={retry} />
          </Card>
          <Heading scale="lg" mt="32px" mb="12px">
            {t('Transactions')}
          </Heading>
          {transactions ? <TransactionTable transactions={transactions} /> : <DataStateBar meta={detail!.transactions.meta} compact />}
        </>
      )}
    </Box>
  )
}
export default TokenDetailView
